import React from 'react';
import produce from 'immer';

import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {SelectReact} from 'common/components/select';
// eslint-disable-next-line import/no-cycle
import {BucketPaneReact} from 'common/widgets/bucket-pane';
import {DefaultCustomBucket} from 'common/query-builder/query-entry-section/constants';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {QueryEntrySectionTrackingEvent} from 'common/query-builder/query-entry-section/tracking';
import {useTranslation} from 'common/i18n/hooks';
import {useFeatureRollout} from 'common/hooks/useFeatureRollout';
import {FeatureRolloutName} from 'common/report/util/feature-sampling';

import type {RowProps} from '../types/catalog';

export type CustomizeBucketsProps = RowProps & {
  isOpen: boolean;
  toggleOpen: () => void;
};

export const CustomizeBuckets = (props: CustomizeBucketsProps) => {
  const {t} = useTranslation();

  const hasTextAnalysis = useFeatureRollout(FeatureRolloutName.TextAnalysis);

  const getBucketLabel = (): string | null => {
    const bucket = props.entry?.behavior.customBucket;
    if (bucket) {
      return `Custom bucket size **${bucket.bucketSize} between ${bucket.min}-${bucket.max}**`;
    }
    return null;
  };
  const bucketLabel = getBucketLabel();

  const onMenuChange = () => {
    props.toggleOpen();
  };

  const updateCustomBucket = (newCustomBucket: Bucket | null) => {
    const updatedEntry = produce(props.entry, (draft) => {
      draft.behavior.customBucket = newCustomBucket;
    });
    props.updateEntry(updatedEntry);
    props.toggleOpen();

    const currentCustomBucket = props.entry.behavior.customBucket;
    if (newCustomBucket) {
      props.dispatchTrack(QueryEntrySectionTrackingEvent.ApplyCustomBucket, {
        ...props.getEntryTrackingProps(),
        'Bucket min': newCustomBucket.min,
        'Bucket max': newCustomBucket.max,
        'Bucket size': newCustomBucket.bucketSize,
        'Is editing': !!currentCustomBucket,
      });
    }
  };

  const fallbackLabel = hasTextAnalysis
    ? t(`query-builder.actions.group-data`)
    : t(`query-builder.actions.customize-buckets`);

  const bucket = props.entry?.behavior.customBucket;

  return bucketLabel || props.isOpen ? (
    <SelectReact
      className='subitem-select'
      label={bucketLabel || fallbackLabel}
      open={props.isOpen}
      theme={SelectTheme.Tertiary}
      alwaysShowCaret
      size={ButtonSize.Small}
      preventClose
      onMenuChange={onMenuChange}
    >
      <div slot='body'>
        {props.isOpen && (
          // @todo(metrics) Rewrite BucketPane to split up the segment vs
          // event use cases instead of jamming them both together in one component
          // with two very different use cases.
          <BucketPaneReact
            eventNames={props.sectionProps.eventNames || []}
            header={t(`query-builder.actions.customize-range`)}
            maxNumBuckets={1000}
            onChangeSelectedBucket={(updatedBucket) => updateCustomBucket(updatedBucket.detail)}
            onResetBucket={() => updateCustomBucket(null)}
            resetBucket={Boolean(bucket)}
            selectedBucket={bucket || DefaultCustomBucket}
            selectedUnit='units'
            units={undefined}
            unitsAsProperty={false}
          />
        )}
        {/* TODO: Use BasicBucketPane directly once BucketPane is rewritten.
          We'll keep using the old one to make sure everything still works. */}
        {/* {props.isOpen && (
            <BasicBucketPane
              eventNames={props.sectionProps.eventNames || []}
              header={t(`query-builder.actions.customize-range`)}
              maxNumBuckets={1000}
              onChangeSelectedBucket={(updatedBucket) => updateCustomBucket(updatedBucket)}
              onResetBucket={() => updateCustomBucket(null)}
              resetBucket={Boolean(bucket)}
              selectedBucket={bucket || DefaultCustomBucket}
              selectedUnit='units'
              units={undefined}
              unitsAsProperty={false}
            />
          )} */}
      </div>
    </SelectReact>
  ) : null;
};
